import React, { useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  BarChart as MetricsIcon,
  FileDownload as DownloadIcon,
} from "@mui/icons-material";

import type { SystemMetricsReport } from "../services/distributionService";

function buildCsv(report: SystemMetricsReport): string {
  const k = report.kpis;
  const rows: string[][] = [
    ["metric", "value"],
    ["generated_at", report.generated_at],
    ["backlog_vigente", String(k.backlog_vigente)],
    ["backlog_expirado_acumulado", String(k.backlog_expirado_acumulado)],
    [
      "denegaciones_cumplidas_ratio",
      k.denegaciones_cumplidas_ratio.toFixed(4),
    ],
    [
      "no_stock_al_momento_pct_30d",
      k.no_stock_al_momento_pct_30d.toFixed(2),
    ],
    [
      "tiempo_ciclo_promedio_dias",
      k.tiempo_ciclo_promedio_dias.toFixed(2),
    ],
    [
      "cobertura_dci_promedio",
      k.cobertura_dci_promedio.toFixed(2),
    ],
    [
      "ventas_vs_cobertura_correlation",
      k.ventas_vs_cobertura_correlation === null
        ? ""
        : k.ventas_vs_cobertura_correlation.toFixed(4),
    ],
    [
      "auto_resolucion_diffs_pct_30d",
      k.auto_resolucion_diffs_pct_30d.toFixed(2),
    ],
  ];
  for (const fr of k.fill_rate_by_store) {
    rows.push([`fill_rate:${fr.store}`, fr.fill_rate.toFixed(4)]);
  }
  return rows
    .map((r) =>
      r.map((c) => `"${c.replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");
}

function downloadCsv(report: SystemMetricsReport): void {
  const csv = buildCsv(report);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `metricas_${report.generated_at.slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

import { usePageTitle } from "../context/PageTitleContext";
import { useSystemMetrics } from "../hooks/useDistributionAnalytics";

const KPI_META = {
  auto_resolution_pct: { target: 85, label: "Auto-resolución diffs" },
  denegaciones_cumplidas_pct: { target: 80, label: "Denegaciones cumplidas" },
  fill_rate_pct: { target: 90, label: "Fill rate" },
};

function Kpi(props: {
  label: string;
  value: string;
  hint?: string;
  status?: "success" | "warning" | "error";
}): React.ReactElement {
  const statusColor = {
    success: "success.main",
    warning: "warning.main",
    error: "error.main",
    undefined: "text.primary",
  } as const;
  const color =
    statusColor[props.status as keyof typeof statusColor] ?? "text.primary";
  return (
    <Card variant="outlined" sx={{ minWidth: 220, flex: "1 1 220px" }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {props.label}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color }}>
          {props.value}
        </Typography>
        {props.hint && (
          <Typography variant="caption" color="text.secondary">
            {props.hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * B3.37 — MetricasExitoPage
 *
 * Dashboard con los 9 KPIs del sistema (§11 lluvia). Se refresca cada 5
 * min vía useSystemMetrics. `cobertura_dci_promedio` y
 * `ventas_vs_cobertura_correlation` se construyen mensualmente desde
 * `monthly_product_store_sales` con umbral N tiendas y ventana lookback
 * configurables vía `distribution_policies` (A12).
 */
export function MetricasExitoPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const { data, isLoading, refetch: _refetch } = useSystemMetrics();

  useEffect(() => {
    setTitle("Distribución — Métricas de éxito");
    return () => setTitle("");
  }, [setTitle]);

  if (isLoading || !data) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const k = data.kpis;
  const cumplidasPct = k.denegaciones_cumplidas_ratio * 100;

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ justifyContent: "space-between" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <MetricsIcon color="primary" />
            <Typography variant="h4" component="h1" fontWeight={600}>
              Métricas de éxito
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => downloadCsv(data)}
          >
            Exportar CSV
          </Button>
        </Stack>

        <Alert severity="info" variant="outlined">
          Los 9 KPIs del §11 de la lluvia se muestran actualizados cada 5
          min. Targets: auto-resolución ≥ {KPI_META.auto_resolution_pct.target}%,
          denegaciones cumplidas ≥ {KPI_META.denegaciones_cumplidas_pct.target}%,
          fill rate por tienda ≥ {KPI_META.fill_rate_pct.target}%.
        </Alert>

        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 2 }}>
          <Kpi
            label="Backlog vigente"
            value={k.backlog_vigente.toLocaleString("es-MX")}
            hint="pending_purchase + purchased"
          />
          <Kpi
            label="Backlog expirado (acum)"
            value={k.backlog_expirado_acumulado.toLocaleString("es-MX")}
            hint="meta: 0 post-deploy"
            status={k.backlog_expirado_acumulado === 0 ? "success" : "warning"}
          />
          <Kpi
            label="Denegaciones cumplidas"
            value={`${cumplidasPct.toFixed(1)}%`}
            hint={`meta ≥ ${KPI_META.denegaciones_cumplidas_pct.target}%`}
            status={
              cumplidasPct >= KPI_META.denegaciones_cumplidas_pct.target
                ? "success"
                : cumplidasPct >= 60
                  ? "warning"
                  : "error"
            }
          />
          <Kpi
            label="no_stock_al_momento (30d)"
            value={`${k.no_stock_al_momento_pct_30d.toFixed(1)}%`}
            hint="meta: tendencia a la baja"
          />
          <Kpi
            label="Tiempo ciclo promedio"
            value={`${k.tiempo_ciclo_promedio_dias.toFixed(1)}d`}
            hint="denial → fulfilled"
          />
          <Kpi
            label="Cobertura DCI promedio"
            value={`${(k.cobertura_dci_promedio * 100).toFixed(1)}%`}
            hint="productos en ≥ N tiendas / total con ventas"
          />
          <Kpi
            label="Auto-resolución diffs (30d)"
            value={`${k.auto_resolucion_diffs_pct_30d.toFixed(1)}%`}
            hint={`meta ≥ ${KPI_META.auto_resolution_pct.target}%`}
            status={
              k.auto_resolucion_diffs_pct_30d >=
              KPI_META.auto_resolution_pct.target
                ? "success"
                : "warning"
            }
          />
          <Kpi
            label="Ventas vs cobertura"
            value={
              k.ventas_vs_cobertura_correlation === null
                ? "—"
                : k.ventas_vs_cobertura_correlation.toFixed(2)
            }
            hint="Pearson(cobertura, ventas) −1..1; null si pocos meses o varianza nula"
          />
        </Stack>

        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Fill rate por tienda
            </Typography>
            <Typography variant="caption" color="text.secondary">
              fulfilled / total denials (todas las fechas). Meta ≥{" "}
              {KPI_META.fill_rate_pct.target}%.
            </Typography>
          </Box>
          {k.fill_rate_by_store.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              Sin data de denegaciones. Requiere al menos 1 ciclo end-to-end.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tienda</TableCell>
                    <TableCell align="right">Fill rate</TableCell>
                    <TableCell align="right">Meta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {k.fill_rate_by_store
                    .sort((a, b) => b.fill_rate - a.fill_rate)
                    .map((r) => {
                      const pct = r.fill_rate * 100;
                      const ok = pct >= KPI_META.fill_rate_pct.target;
                      return (
                        <TableRow key={r.store}>
                          <TableCell>{r.store}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: ok ? "success.main" : "warning.main",
                              fontWeight: 600,
                            }}
                          >
                            {pct.toFixed(1)}%
                          </TableCell>
                          <TableCell align="right">
                            {KPI_META.fill_rate_pct.target}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Typography variant="caption" color="text.secondary">
          Generado: {new Date(data.generated_at).toLocaleString("es-MX")}
        </Typography>
      </Stack>
    </Box>
  );
}
